import ProductDetailSheetLoader from "./ProductDetailSheetLoader";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import EmptyData from "@/components/state-pages/EmptyData";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import * as Sheet from "@/components/ui/sheet";
import * as Table from "@/components/ui/table";
import { ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { ProductType } from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";
import { useGetSingleProductQuery } from "@/redux/api/toolsQuery";

interface ProductOnSiteSheetProps {
  isOpen: boolean;
  toggleOnSiteSheet: () => void;
}

const ProductOnsiteSheet = ({
  isOpen,
  toggleOnSiteSheet,
}: ProductOnSiteSheetProps): JSX.Element => {
  const { theme: skin } = useTheme();
  const commonClasses = cn(
    "text-primary uppercase  border-r border-r-white ",
    skin === "dark" && "text-white",
    skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight",
  );

  const { id, reset } = useQueryID(ToolsKeys.PRODUCT_ASSIGNMENT_ID);
  const { data, ...rest } = useGetSingleProductQuery(
    {
      id,
    },
    {
      skip: !id || !isOpen,
    },
  );
  const productData = data?.data;
  const type = productData?.type;

  const handleExit = (): void => {
    reset();
    toggleOnSiteSheet();
  };

  return (
    <Sheet.Sheet open={isOpen} onOpenChange={handleExit}>
      <Sheet.SheetContent className='min-w-[45dvw] space-y-3'>
        <Sheet.SheetHeader className=' text-xl font-medium'>
          OnSite
        </Sheet.SheetHeader>
        <FetchWrapper {...rest} loaderComp={<ProductDetailSheetLoader />}>
          <section className='space-y-4'>
            <h2 className='text-[#717171] font-medium'>
              Product Name:{" "}
              <Button
                variant={"outline"}
                className='ml-2 hover:bg-transparent text-[#637381]'
              >
                {productData?.name || "-"}
              </Button>
            </h2>
            <section>
              <div className='rounded-md overflow-clip border'>
                <Table.Table>
                  <Table.TableHeader
                    className={cn(
                      skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight",
                    )}
                  >
                    <Table.TableRow>
                      <Table.TableHead className={cn(commonClasses)}>
                        Project Name
                      </Table.TableHead>
                      <Table.TableHead className={commonClasses}>
                        Sub Products
                      </Table.TableHead>
                      <Table.TableHead className={commonClasses}>
                        Quantity
                      </Table.TableHead>
                    </Table.TableRow>
                  </Table.TableHeader>
                  <Table.TableBody className=''>
                    {productData?.productAssignments?.length ? (
                      productData?.productAssignments.map(
                        (singleAssignment) => {
                          const commonCellClassName =
                            "bg-white hover:bg-white border-r border-r-gray-100";
                          const { project, assignedQuantity } =
                            singleAssignment;

                          return (
                            <Table.TableRow key={project?.id}>
                              <Table.TableCell className={commonCellClassName}>
                                {project?.name ?? "-"}
                              </Table.TableCell>
                              <Table.TableCell className={commonCellClassName}>
                                {type === ProductType.Has_Sub_Product
                                  ? assignedQuantity
                                  : "-"}
                              </Table.TableCell>
                              <Table.TableCell className={commonCellClassName}>
                                {type === ProductType.Has_Quantity
                                  ? assignedQuantity
                                  : "-"}
                              </Table.TableCell>
                            </Table.TableRow>
                          );
                        },
                      )
                    ) : (
                      <Table.TableRow>
                        <Table.TableCell colSpan={3}>
                          <EmptyData title='On Site tools' />
                        </Table.TableCell>
                      </Table.TableRow>
                    )}
                  </Table.TableBody>
                </Table.Table>
              </div>
            </section>
          </section>
        </FetchWrapper>
      </Sheet.SheetContent>
    </Sheet.Sheet>
  );
};

export default ProductOnsiteSheet;
